import type {
	Flashcard,
	FlashcardInfo,
	FlashcardSet,
	FlashcardSetInfo,
	FlashcardSetPopulated,
	FlashcardSetRepetition,
	FlashcardSetStreamingChunk,
	GistPopulated,
	Modify,
	NotePopulated,
	Share,
	ApiResponse,
	UniqueId,
} from "@vita-learning/types";

import { makeUrl, revalidate } from "../../../utils";

import { resHttpClient, aiHttpClient, type HttpConfig } from "../../config";
import { assertCorrectUrlEndpoint } from "../../utils";

import { decodeFlashcardSet } from "./helpers";

export const createFlashcardSet = async (
	flashcardSet: FlashcardSetInfo | FlashcardSet
): Promise<FlashcardSetPopulated> => {
	const resp = await resHttpClient.post<ApiResponse<FlashcardSetPopulated>>(
		"/flashcards",
		flashcardSet
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return decodeFlashcardSet(resp.data);
};

export const createManyFlashcardSets = async (
	flashcardSets: (FlashcardSetInfo | FlashcardSet)[]
): Promise<FlashcardSetPopulated[]> => {
	const resp = await resHttpClient.post<ApiResponse<FlashcardSetPopulated[]>>(
		"/flashcards?bulk=true",
		flashcardSets
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return resp.data.map(decodeFlashcardSet);
};

export const createFlashcardsShareUrl = async (
	payload: UniqueId
): Promise<{ url: string }> => {
	const resp = await resHttpClient.post<ApiResponse<{ url: string }>>(
		"/flashcards/share-urls",
		payload
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data;
};

export const getFlashcardSet = async (
	id: string,
	config?: HttpConfig
): Promise<FlashcardSetPopulated> => {
	const resp = await resHttpClient.get<ApiResponse<FlashcardSetPopulated>>(
		`/flashcards/${id}`,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return decodeFlashcardSet(resp.data);
};

export const fetchFlashcardSets = async (
	url: string,
	config?: HttpConfig
): Promise<FlashcardSetPopulated[]> => {
	assertCorrectUrlEndpoint(url, "/flashcards");

	const resp = await resHttpClient.get<
		ApiResponse<FlashcardSetPopulated[] | null>
	>(url, config);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data?.map(decodeFlashcardSet) || [];
};

/**
 *
 * @param flashcardSet
 * @returns
 */
export const updateFlashcardSet = async (
	flashcardSet: Modify<
		FlashcardSetPopulated,
		Record<"cards", Array<FlashcardInfo | Flashcard>>
	>
): Promise<FlashcardSetPopulated> => {
	if (!flashcardSet.id) throw new Error("Flashcard set ID is required");

	const resp = await resHttpClient.put<ApiResponse<FlashcardSetPopulated>>(
		`/flashcards/${flashcardSet.id}`,
		flashcardSet
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return decodeFlashcardSet(resp.data);
};

/**
 * Updates a flashcard set repetition by its ID.
 * @param id - The ID of the flashcard set.
 * @param repetition - The repetition data to update.
 * @returns The updated flashcard set repetition.
 */
export const updateFlashcardSetRepetition = async (
	id: string,
	repetition: FlashcardSetRepetition
): Promise<FlashcardSetRepetition> => {
	const resp = await resHttpClient.patch<ApiResponse<FlashcardSetRepetition>>(
		`/flashcards/${id}/repetitions`,
		repetition
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return resp.data;
};

export const shareFlashcardSet = async (
	id: string,
	share: Share
): Promise<FlashcardSetPopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<FlashcardSetPopulated>>(
		`/flashcards/${id}/share`,
		{ ...share, id }
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return decodeFlashcardSet(resp.data);
};

export const deleteFlashcardSet = async (id: string) => {
	const resp = await resHttpClient.delete<ApiResponse<void>>(
		`/flashcards/${id}`
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("flashcards");

	return resp.data;
};

export const generateFlashcardSet = (
	flashcardSet: Partial<FlashcardSet> &
		(
			| {
					file?: {
						id: string;
						userId: string;
						title: string;
						ext: string;
						url: string;
						expiresAt: string;
					};
			  }
			| {
					note?: NotePopulated;
			  }
			| {
					url?: string;
			  }
		),
	source: "file" | "note" | "url",
	onStream: (chunk: FlashcardSetStreamingChunk) => void
) => {
	const url = makeUrl("/flashcards");

	url.searchParams.set("source", source);

	return aiHttpClient.post<void>(url.pathname + url.search, flashcardSet, {
		headers: {
			// Accept server-sent events
			Accept: "text/event-stream",
			// Disabling compression for streaming responses
			"Accept-Encoding": "identity",
			// Send the data as JSON
			"Content-Type": "application/json",
			// Preventing caching
			"Cache-Control": "no-cache",
		},
		cache: "no-cache",
		onStream: chunk => {
			// Chunks might accumulate so split them by the new line
			const lines = chunk.split("\n").filter(line => line.trim() !== "");
			const DATA_CHUNK_PREFIX = "data: ";
			const ERROR_CHUNK_PREFIX = "error: ";

			lines.forEach(line => {
				try {
					if (line.startsWith(DATA_CHUNK_PREFIX))
						onStream(
							JSON.parse(
								line.replace(DATA_CHUNK_PREFIX, "").trim()
							) as FlashcardSetStreamingChunk
						);

					if (line.startsWith(ERROR_CHUNK_PREFIX))
						throw JSON.parse(line.replace(ERROR_CHUNK_PREFIX, "").trim());
				} catch (e) {
					if (!(e instanceof SyntaxError)) throw e;

					console.error(e);
				}
			});
		},
	});
};
